import React, { FC, useCallback } from 'react'
import { x } from '@xstyled/styled-components'
import { FormikProps, FormikValues } from 'formik'
import { GridActionsType, GridSelectorsType } from '@mailstep/design-system/ui/Blocks/CommonGrid/types'
import { ProductApiRead } from '@typings/entities/Product'
import { ProductStockApi, ProductStockApiLot } from '@typings/entities/ProductStock'
import { TransferDetailItem } from '@typings/entities/Transfer'
import { formikCallback } from '@typings/Formik'
import { Trans } from '@lingui/react'
import getOr from 'lodash/fp/getOr'
import { createActionColumnDefinition, createColumnDefinitions } from './TableDefinition'
import { AddProductItem, FormGrid, useFormGridChange } from '@components/blocks/FormGrid'
import { useItemsQuantity } from '@hooks/useItemsQuantity'
import Fieldset from '@designSystem/Fieldset'
import { FileSubmit, OnDownloadTemplate } from '@typings/file'
import FormGridTitle from '@components/elements/FormGridTitle'
import { duplicateItemMerge, createEmptyItem } from '@components/forms/utils/FormItems'
import Modal from '@mailstep/design-system/ui/Blocks/Modal'
import { StyledSubTitle } from '@components/elements/Typography/lib'
import LotsList from '@components/blocks/LotsList'
import { useModal } from '@mailstep/design-system/ui/Blocks/Modal/hooks/useModal'
import { isMetaLot } from '@utils/expedition'
import { StockChangeDetailItem } from '@typings/entities/StockChange'

interface TransferItemsFieldProps {
  form: FormikProps<FormikValues>
  loadProducts: (fulltext: string) => Promise<ProductApiRead[]>
  loadProductStocks: (productIds: string[], warehouse: string, wms: string) => Promise<ProductStockApi[]>
  warehouse?: string
  wms?: string
  gridSelectors: GridSelectorsType
  gridActions: GridActionsType
  isEditable?: boolean
  setFieldValue: formikCallback
  onImportItems: FileSubmit<{ product: ProductApiRead; quantity: number }[]>
  onDownloadImportTemplate: OnDownloadTemplate
  newBookingEnabled: boolean
  lotsGridActions: GridActionsType
  lotsGridSelectors: GridSelectorsType
  replace: (index: number, value: any) => void
}

const customMergeId = (item: TransferDetailItem): string =>
  `${item.product.id}|${
    (item.assignedLot || item.lot) && !isMetaLot(item.assignedLot || item.lot) ? item.assignedLot || item.lot : ''
  }`

const TransferItemsField: FC<TransferItemsFieldProps> = ({
  form,
  loadProducts,
  loadProductStocks,
  warehouse,
  wms,
  gridSelectors,
  gridActions,
  isEditable,
  newBookingEnabled,
  lotsGridActions,
  lotsGridSelectors,
  replace,
  setFieldValue,
  onImportItems,
  onDownloadImportTemplate,
}) => {
  const itemsRaw: TransferDetailItem[] = getOr([], 'values.items', form)
  const error = getOr(null, 'errors.items', form)

  const { allItems, isLoading: isQtyLoading } = useItemsQuantity<TransferDetailItem>(
    itemsRaw,
    warehouse,
    wms,
    setFieldValue,
    loadProductStocks,
  )
  const {
    onOpen: setLotsVisible,
    isOpen: lotsVisible,
    onClose: closeLots,
    data: transferItem,
  } = useModal<TransferDetailItem | null>()

  const setItems = useCallback((items: TransferDetailItem[]) => setFieldValue('items', items, false), [setFieldValue])
  const { onAdd, onRemove, getIndex } = useFormGridChange<TransferDetailItem>({
    gridSelectors,
    gridActions,
    allItems,
    itemIdentifier: ['product.id', 'assignedLot', 'lot'],
    setItems,
    duplicateItemMerge,
    customMergeId,
  })

  const onRowAction = useCallback(
    (id: string, field: string, row: TransferDetailItem): void => {
      if (field === 'remove') onRemove([row])
      if (field === 'choseLot') setLotsVisible(row)
    },
    [onRemove, setLotsVisible],
  )

  const setLotForRow = useCallback(
    (lot: ProductStockApiLot['lot']) => {
      if (!transferItem) return
      const extraValues: Partial<StockChangeDetailItem> = {}
      // if selecting lot also wipe out pairing - items with specific lot can not be paired
      if (!isMetaLot(lot)) {
        extraValues.bookStockAdvices = []
        extraValues.bookAdviceTotal = 0
      }
      replace(getIndex(transferItem), { ...transferItem, lot, ...extraValues })
      closeLots()
    },
    [transferItem, closeLots, getIndex, replace],
  )

  const columnsDefinitions = createColumnDefinitions(!!isEditable, getIndex)

  const errorString = error && typeof error === 'string' && form.submitCount > 0 ? error : null

  return (
    <Fieldset disabled={isQtyLoading} fullWidth>
      <FormGridTitle title={<Trans id="form.tranGridImportDialogems.heading" message="Transfer Items" />} error={errorString} />

      {isEditable && (
        <AddProductItem<TransferDetailItem>
          onAdd={onAdd}
          loadProducts={loadProducts}
          formatGridItem={createEmptyItem}
          onImportItems={onImportItems}
          onDownloadImportTemplate={onDownloadImportTemplate}
          canAddLotProducts={newBookingEnabled}
        />
      )}

      <x.div mt={3} mb={3}>
        <FormGrid
          items={allItems}
          columnsDefinitions={columnsDefinitions}
          actionColumnDefinition={createActionColumnDefinition()}
          gridSelectors={gridSelectors}
          gridActions={gridActions}
          onRowAction={onRowAction}
        />
      </x.div>

      {lotsVisible && (
        <Modal
          onCancel={closeLots}
          hasFooter={false}
          width="medium"
          title={<Trans id="chooseLot" message="Choose a lot" />}
          cancelLabel={<Trans id="form.buttonCancel" message="Cancel" />}
        >
          <>
            <x.div mt={4} pb={3}>
              <StyledSubTitle>
                <Trans id="lotItems" message="LOT items" />
              </StyledSubTitle>
            </x.div>
            <LotsList
              stockLots={transferItem?.stockLots}
              gridActions={lotsGridActions}
              gridSelectors={lotsGridSelectors}
              onLotSelect={setLotForRow}
            />
          </>
        </Modal>
      )}
    </Fieldset>
  )
}

export default TransferItemsField
